import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { PubnubChannel } from 'components/sharpr/pubnub/PubnubChannel';
import { ensure } from 'components/sharpr/ensure/ensure.js';
import _ from 'lodash';

let permsData = [];
let userData = {};

/**
 * Add controller resolves for getting user permission and identity
 * @param {Object} resolves  The other resolves for the ui-router controller
 */
export function IdentityResolves(resolves) {
	resolves.__identityPermissions = ApiResourcify.endpoint('/users/permissions', {
		withResponse: resp => (permsData = resp.data || []),
		cacheFor: 5 * 60 * 1000,
	});
	resolves.__identityMe = ApiResourcify.endpoint('/v3/users/me', {
		withResponse: async resp => {
			if (resp.data.id && !resp.data.uuid) {
				// using fetch here because this is an older part of the codebase that will be updated and
				// I really didn't fancy trying to figure out the dependency injection dance to chain the
				// uuid call after the main one;  ApiResourcify doesn't lend itself well to one-off uses...
				const {
					payload: { uuid },
				} = await fetch(window.BASE_URL + '/api/v4/users/' + resp.data.id + '/uuid', {
					headers: {
						Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('jwt')).token,
					},
				}).then(response => response.json());
				resp.data.uuid = uuid;
			}

			return _.extend(userData, resp.data || {});
		},
		cacheFor: 5 * 60 * 1000,
	});
}

/**
 * Intercept ui-router controller activation and block if user has no permission
 * @param {Object} $rootScope  The angular rootScope, where the ui-router broadcasts its $stateChangeStart event
 * @param {Object} notifications  The ng-notifications-bar service used to alert user that access is denied
 */
export function restrictRouteByPermission($rootScope, notifications, $state, ErrorScreen) {
	'ngInject';
	// check the controller that is about to be loaded
	$rootScope.$on('$stateChangeStart', function (event, toState, toParams, fromState, fromParams) {
		if (!IdentityService.canRoute(toState)) {
			// tell ui-router not to continue to route
			event.preventDefault();
			// let user know what happened
			notifications.showError({ message: 'You are not allowed to access that page.' });
		}
	});
	// also check after controller is loaded so we can handle the case where userData hasn't been populated yet
	// e.g. pasting a url in a new tab for a forbidden page
	$rootScope.$on('$stateChangeSuccess', function (event, toState, toParams, fromState, fromParams) {
		if (!IdentityService.canRoute(toState)) {
			if (userData.role === 'reader') {
				document.querySelector('main').remove();

				const defaultHub = ensure.array(userData.hubs).find(hub => hub.id === userData.default_client_id);
				if (defaultHub) {
					const thisPage = encodeURIComponent(window.location.href);
					window.location.href = `/users/setclient/${defaultHub.id}?backTo=${thisPage}`;
				} else {
					const error = {
						class: 'not-allowed',
						title: "This page isn't for you",
						message: "For some reason, you don't have access to this page.",
						actionText: 'Log in as a different user',
						blockHiding: true,
						actionUrl: '/logout',
					};
					ErrorScreen.show(error);
				}
			} else {
				// go to the default state (post listing page)
				const defaultStateName = window.isMobile ? 'mobile.loggedin.PostListing' : 'hub.loggedin.PostListing'; // TODO: go to preferred home page?
				$state.go(fromState.name || defaultStateName, fromParams || {});
			}
		}
	});
}

/**
 * Manage user identity
 */
export class IdentityService {
	/**
	 * @param Api  The Api Service
	 */
	constructor(Api, Taxonomy, PartnerLinks, Auth, Cfg, SecureStorage, $q) {
		'ngInject';
		/**
		 * The Api Service
		 * @var {Api}
		 */
		this.Api = Api;
		/**
		 * The Taxonomy Service
		 * @var {TaxonomyService}
		 */
		this.Taxonomy = Taxonomy;
		/**
		 * The PartnerLinks Service
		 * Needed to re-fetch partner links when hub changes
		 * @var {PartnerLinksService}
		 */
		this.PartnerLinks = PartnerLinks;
		/**
		 * The Auth Service
		 * @var {AuthService}
		 */
		this.Auth = Auth;
		/**
		 * The Cfg service
		 * @var {CfgService}
		 */
		this.Cfg = Cfg;
		/**
		 * Instance of SecureStorageService for storing user ID in keychain on iOS
		 * {SecureStorage} secureStorage
		 */
		this.secureStorage = SecureStorage;
		/**
		 * Angular's $q Service
		 */
		this.$q = $q;
		// set up empty spaces for pubnub
		this._initPubnubSkeleton();
	}

	/**
	 * Load permissions and data for the current user.
	 * During testing, IdentityResolves() is never called. Calling load()
	 * allows us to populate the user data on demand.
	 * @returns {Promise}
	 */
	load() {
		return this.$q.all([
			this.Api.get('/users/permissions').then(data => (permsData = data)),
			this.Api.get('/v3/users/me').then(async data => {
				if (!data.uuid) {
					const { uuid } = await this.Api.get('/v4/users/' + data.id + '/uuid');

					data.uuid = uuid;
				}

				this.setUser(data);
				this.secureStorage.set('user_id', String(data.id));
				ApiResourcify.clearCache('/v3/users/me');
			}),
		]);
	}

	/**
	 * Is the user a superadmin? I.e. a Sharpr employee
	 * @returns {Boolean}
	 */
	isSuperadmin() {
		return userData.is_superadmin;
	}

	/**
	 * Can the user approve posts in the post approval workflow?
	 * @returns {Boolean}
	 */
	isApprover() {
		return userData.is_approver;
	}

	/**
	 * Is the user an account manager over the current hub?
	 * @return {Boolean}
	 */
	isAccountManager() {
		return !userData.is_superadmin && permsData.indexOf('manage_hubs') > -1;
	}

	/**
	 * Is the given action allowed by the current user
	 * @param {String} action  The name of the action (defined in app/libs/Permissions.php)
	 * @returns {Boolean}
	 */
	can(action) {
		return userData.is_superadmin || permsData.indexOf(action) > -1;
	}

	/**
	 * Are ANY of the given actions allowed by the current user
	 * @param {String[]} actions  Action names as defined in app/libs/Permissions.php
	 * @returns {Boolean}
	 */
	canAny(actions) {
		return actions.some(this.can.bind(this));
	}

	/**
	 * Are ALL of the given actions allowed by the current user
	 * @param {String[]} actions  Action names as defined in app/libs/Permissions.php
	 * @returns {Boolean}
	 */
	canAll(actions) {
		return actions.every(this.can.bind(this));
	}

	/**
	 * Check if a user can route to the given ui-router state
	 * @param {Object} toState  The ui-router state object
	 * @return {boolean}
	 */
	static canRoute(toState) {
		if (
			// permissions data is not loaded yet; canRoute() will be called again after $stateChangeSuccess
			permsData.length === 0 ||
			// user data is not loaded yet; canRoute() will be called again after $stateChangeSuccess
			!userData.email ||
			// user is superadmin and can access everything
			userData.is_superadmin ||
			// controller state does not have a permission restriction
			!Array.isArray(toState.permission)
		) {
			return true;
		}
		// check that user has every permission listed in the controller route
		return toState.permission.every(action => {
			return permsData.indexOf(action) > -1;
		});
	}

	/**
	 * Get the user data
	 * @returns {Object}
	 * @property {Number} id
	 * @property {String} fname
	 * @property {String} lname
	 * @property {String} fname_preferred
	 * @property {String} lname_preferred
	 * @property {String} initials
	 * @property {String} email
	 * @property {String} avatar  URL to avatar image
	 * @property {String} role    Permission level
	 * @property {Boolean} is_active
	 * @property {Date} last_login
	 * @property {Date} created_at
	 * @property {Date} modified_at
	 */
	user() {
		return userData;
	}

	/**
	 * Extend the current user object with the given values
	 * @param {Object} user  Key-pairs to set on the user object
	 * @returns {IdentityService}
	 * @chainable
	 */
	setUser(user) {
		_.extend(userData, user);
		this._reInitPubnubChannels();
		return this;
	}

	/**
	 * Get the role name of the current user
	 * @returns {String}
	 */
	role() {
		return userData.role;
	}

	/**
	 * Return true if the current user's role is "executive" (a.k.a. "client")
	 * @returns {Boolean}
	 */
	isExecutive() {
		return userData.role == 'executive';
	}

	/**
	 * Get the hub currently in use
	 * @returns {Object}
	 */
	currentClient() {
		if (!userData || !userData.hubs) {
			return false;
		}
		let current = _.find(userData.hubs, { current: true });
		return current ? _.cloneDeep(current) : false;
	}

	/**
	 * Get the Account ID from the current hub
	 * @returns {Number}
	 */
	currentAccountId() {
		return this.currentAffiliationId();
	}

	/**
	 * Get the Account ID currently in use
	 * @returns {Object}
	 */
	currentAffiliationId() {
		if (!userData || !userData.hubs || userData.hubs.length === 0) {
			return false;
		}
		let current = _.find(userData.hubs, { current: true });
		return current ? current.account_id : userData.hubs[0].account_id;
	}

	/**
	 * Check if user belongs to more than one hub
	 * @returns {Boolean}
	 */
	hasMultipleHubs() {
		return userData && userData.hubs && userData.hubs.length > 1;
	}

	/**
	 * Get the list of allowed hubs
	 * @returns {Object[]}
	 */
	allowedClients() {
		return _.cloneDeep(userData.hubs || []);
	}

	/**
	 * Get the IDs of the user's allowed hubs
	 * @returns {Number[]}
	 */
	allowedClientIds() {
		return _.pluck(userData.hubs || [], 'id');
	}

	/**
	 * Get all the hub records for the current account
	 * @return {Object[]}
	 */
	hubsInThisAccount() {
		const acctId = this.currentAffiliationId();
		const hubs = ensure.array(userData.hubs).filter(hub => hub.account_id == acctId);
		return _.cloneDeep(hubs);
	}

	/**
	 * Get the IDs of every hub in the current account
	 * @return {Array}
	 */
	hubIdsInThisAccount() {
		const acctId = this.currentAffiliationId();
		const hubs = ensure.array(userData.hubs).filter(hub => hub.account_id == acctId);
		return _.pluck(hubs, 'id');
	}

	/**
	 * Get a list of allowed accounts suitable for use with <sh-typeahead-*> components
	 * @returns {Array}  Each an object with properties id, $search
	 */
	accountChoices() {
		if (!userData || !userData.hubs) {
			return [];
		}
		const accountsById = {};
		userData.hubs.forEach(hub => {
			accountsById[hub.account_id] = {
				id: Number(hub.account_id),
				$label: hub.account,
				$search: `@${hub.account_id} ${hub.account}`,
			};
		});
		const accounts = _.values(accountsById);
		const sorted = _.sortBy(accounts, '$label');
		return sorted;
	}

	/**
	 * Get a list of allowed hubs suitable for use with <sh-typeahead-*> components
	 * @returns {Array}  Each an object with properties id, $search
	 */
	hubChoices() {
		if (!userData || !userData.hubs) {
			return [];
		}
		const hubs = [];
		userData.hubs.forEach(hub => {
			hubs.push({
				id: hub.id,
				account_id: hub.account_id,
				$label: hub.name,
				$search: `@${hub.id} ${hub.name}`,
			});
		});
		const choices = _.sortBy(hubs, '$label');
		return choices;
	}

	hubsInAccount(accountId = null) {
		const currentAccount = accountId || this.currentClient().account_id;
		const hubs = [];
		userData.hubs.forEach(hub => {
			if (hub.account_id != currentAccount) {
				return;
			}
			hubs.push({
				id: hub.id,
				name: hub.name,
			});
		});
		const choices = _.sortBy(hubs, 'name');
		return choices;
	}

	/**
	 * Return true if the user is currently logged in
	 * @returns {Boolean}
	 */
	isLoggedin() {
		return Boolean(userData && userData.id && userData.hubs && userData.hubs.length);
	}

	/**
	 * Return true if the user is currently logged out
	 * @returns {Boolean}
	 */
	isLoggedout() {
		return !this.isLoggedin();
	}

	/**
	 * Update Identity Service state to reflect hub change
	 * @param {Int} newHubId  Id of the hub to which user is switching
	 * @returns {Promise}  Resolves with data for new hub or with false on error
	 */
	async useHub(newHubId) {
		if (!userData || !userData.hubs) {
			return Promise.resolve({});
		}
		const currentHubIdx = _.findIndex(userData.hubs, { id: this.currentClient().id });
		const newHubIndex = _.findIndex(userData.hubs, { id: newHubId });
		// check if `hubId` in allowedClients
		if (newHubIndex === -1) {
			// they don't belong to that hub
			return Promise.resolve(false);
		}
		const newHub = userData.hubs.filter(hub => hub.id == newHubId);
		if (!newHub[0]) {
			return Promise.resolve(false);
		}
		// change currentClient
		userData.hubs[currentHubIdx].current = false;
		userData.hubs[newHubIndex].current = true;
		// get user's role in new hub
		userData.role = userData.hubs[newHubIndex].role;
		// update permissions based on this new hub
		if (this.Auth.isLoggedIn()) {
			// jwt auth
			const refreshOk = await this.Auth.refresh(newHubId);
			if (!refreshOk) {
				alert('Unable to switch hubs. Please try again.');
				return false;
			}
		}
		// cookie auth and need to ensure Session and Jwt are kept in sync
		await this.Api.post(`/users/go_to_hub/${newHubId}`, {}, { avoidLoadingBar: true });
		// clear relevant caches cache
		ApiResourcify.clearCache();
		this.Taxonomy.clearAll();
		if (this.PartnerLinks && this.PartnerLinks.clear) {
			this.PartnerLinks.clear();
		}
		// load hub configs and user permissions
		await this.Cfg.load(newHub);
		// we must ensure that new cfg is loaded before re-initializing pubnub channels
		this._reInitPubnubChannels();
		return this.Api.get('/users/permissions', {}, { avoidLoadingBar: true })
			.then(data => (permsData = data))
			.then(() => {
				// check if new hub is one we know about
				return userData.hubs[newHubIndex];
			});
	}

	/**
	 * Fire a callback when an account-level message of the given type is fired
	 * @param {string} type  The event name (e.g. "file.has-preview")
	 * @param {Function<Object>} handler  The handler to invoke. Receives an object with message data
	 * @example Identity.onAccountMessage('account.new-info', msg => handleMsg(msg));
	 * @returns {Function}  A function that when invoked de-registers the callback
	 */
	onAccountMessage(type, handler) {
		return this._onChannelMessage('account', type, handler);
	}

	/**
	 * Fire a callback when a hub-level message of the given type is fired
	 * @param {string} type  The event name (e.g. "file.has-preview")
	 * @param {Function<Object>} handler  The handler to invoke. Receives an object with message data
	 * @example Identity.onHubMessage('file.has-preview', msg => handleMsg(msg));
	 * @returns {Function}  A function that when invoked de-registers the callback
	 */
	onHubMessage(type, handler) {
		return this._onChannelMessage('hub', type, handler);
	}

	/**
	 * Fire a callback when an user-level message of the given type is fired
	 * @param {string} type  The event name (e.g. "file.has-preview")
	 * @param {Function<Object>} handler  The handler to invoke. Receives an object with message data
	 * @example Identity.onUserMessage('user.new-notification', msg => handleMsg(msg));
	 * @returns {Function}  A function that when invoked de-registers the callback
	 */
	onUserMessage(type, handler) {
		return this._onChannelMessage('user', type, handler);
	}

	/**
	 * Clear out permission info, user data and ApiResourcify cache
	 */
	clear() {
		permsData.length = 0;
		Object.keys(userData).forEach(key => {
			delete userData[key];
		});
		ApiResourcify.clearCache();
		this.Taxonomy.clearAll();
		this.PartnerLinks.clear();
		this._destroyPubnubChannels();
		this._initPubnubSkeleton();
	}

	/**
	 * Create holding arrays for channels and handlers for account id, hub id and user id
	 * @private
	 */
	_initPubnubSkeleton() {
		this._pubnubChannels = {
			account: undefined,
			hub: undefined,
			user: undefined,
		};
		this._pubnubHandlers = {
			account: [],
			hub: [],
			user: [],
		};
	}

	/**
	 * Destroy all PubNub channels that are set up
	 * @private
	 */
	_destroyPubnubChannels() {
		_.forOwn(this._pubnubChannels, channel => {
			if (channel) {
				channel.destroy();
			}
		});
	}

	/**
	 * Destroy all PubNub channels that are set up
	 * and resubscribe using values from /api/v2/config/preferences
	 * @private
	 */
	_reInitPubnubChannels() {
		_.forOwn(this._pubnubChannels, (channel, level) => {
			if (channel) {
				channel.destroy();
				this._pubnubChannels[level] = null;
				this._initPubnubChannel(level);
			}
		});
	}

	/**
	 * Add a handler for the given level
	 * @param {String} level  Either account, hub, or user
	 * @param {String} type  The event name (e.g. "file.has-text")
	 * @param {Function<Object>} handler  The handler to invoke. Receives an object with message data
	 * @returns {Function}  A function that when invoked de-registers the callback
	 * @private
	 */
	_onChannelMessage(level, type, handler) {
		this._initPubnubChannel(level);
		const deregister = () => {
			const idx = _.findIndex(this._pubnubHandlers[level], { type, handler });
			if (idx > -1) {
				this._pubnubHandlers[level].splice(idx, 1);
			}
		};
		this._pubnubHandlers[level].push({ type, handler, deregister });
		return deregister;
	}

	/**
	 * Set up a PubNub channel for the given level if it isn't yet set up.
	 * It automatically updates the channel at 00:00:00 UTC when channel name changes
	 * @param {String} level  Either account, hub, or user
	 * @private
	 */
	_initPubnubChannel(level) {
		if (this._pubnubChannels[level]) {
			// already set up
			return;
		}
		const channelName = this.Cfg.read(`Pubnub.${level}_channel`);
		// console.log(`_initPubnubChannel ${level}: ${channelName}`);
		this._pubnubChannels[level] = channelName ? new PubnubChannel(channelName, userData.uuid) : PubnubChannel.getStub();
		this._pubnubChannels[level].subscribe((message, metadata) => {
			this._pubnubHandlers[level].forEach(({ type, handler }) => {
				if (message.type === type) {
					try {
						handler(message, metadata);
					} catch (e) {
						console.error('IdentityService hubChannel handler error:', e);
					}
				}
			});
		});
	}
}
